import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Button, Flex, IconButton, Text } from "@chakra-ui/react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <Flex justify="center" align="center" mt={6} gap={2}>
      <IconButton
        aria-label="Previous page"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        variant="ghost"
        icon={<ChevronLeftIcon />}
      />

      {/* Page numbers - show 4 nearest neighbors with ellipsis logic */}
      {(() => {
        const renderPages = [];

        if (totalPages <= 7) {
          // Show all pages if 7 or fewer
          for (let i = 1; i <= totalPages; i++) {
            renderPages.push(
              <Button
                key={i}
                size="sm"
                onClick={() => onPageChange(i)}
                colorScheme={currentPage === i ? "blue" : "gray"}
                variant={currentPage === i ? "solid" : "outline"}
              >
                {i}
              </Button>
            );
          }
        } else if (currentPage <= 3) {
          // Show 1-5 ... last
          for (let i = 1; i <= 5; i++) {
            renderPages.push(
              <Button
                key={i}
                size="sm"
                onClick={() => onPageChange(i)}
                colorScheme={currentPage === i ? "blue" : "gray"}
                variant={currentPage === i ? "solid" : "outline"}
              >
                {i}
              </Button>
            );
          }
          renderPages.push(
            <Text key="ellipsis1" px={2}>
              ...
            </Text>
          );
          renderPages.push(
            <Button
              key={totalPages}
              size="sm"
              onClick={() => onPageChange(totalPages)}
              colorScheme={currentPage === totalPages ? "blue" : "gray"}
              variant={currentPage === totalPages ? "solid" : "outline"}
            >
              {totalPages}
            </Button>
          );
        } else if (currentPage >= totalPages - 2) {
          // Show 1 ... last 3 pages
          renderPages.push(
            <Button
              key={1}
              size="sm"
              onClick={() => onPageChange(1)}
              colorScheme={currentPage === 1 ? "blue" : "gray"}
              variant={currentPage === 1 ? "solid" : "outline"}
            >
              1
            </Button>
          );
          renderPages.push(
            <Text key="ellipsis2" px={2}>
              ...
            </Text>
          );
          for (let i = totalPages - 4; i <= totalPages; i++) {
            renderPages.push(
              <Button
                key={i}
                size="sm"
                onClick={() => onPageChange(i)}
                colorScheme={currentPage === i ? "blue" : "gray"}
                variant={currentPage === i ? "solid" : "outline"}
              >
                {i}
              </Button>
            );
          }
        } else {
          // Show 1 ... current-2, current-1, current, current+1, current+2 ... last
          renderPages.push(
            <Button
              key={1}
              size="sm"
              onClick={() => onPageChange(1)}
              colorScheme={currentPage === 1 ? "blue" : "gray"}
              variant={currentPage === 1 ? "solid" : "outline"}
            >
              1
            </Button>
          );
          renderPages.push(
            <Text key="ellipsis3" px={2}>
              ...
            </Text>
          );

          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            renderPages.push(
              <Button
                key={i}
                size="sm"
                onClick={() => onPageChange(i)}
                colorScheme={currentPage === i ? "blue" : "gray"}
                variant={currentPage === i ? "solid" : "outline"}
              >
                {i}
              </Button>
            );
          }

          renderPages.push(
            <Text key="ellipsis4" px={2}>
              ...
            </Text>
          );
          renderPages.push(
            <Button
              key={totalPages}
              size="sm"
              onClick={() => onPageChange(totalPages)}
              colorScheme={currentPage === totalPages ? "blue" : "gray"}
              variant={currentPage === totalPages ? "solid" : "outline"}
            >
              {totalPages}
            </Button>
          );
        }

        return renderPages;
      })()}

      <IconButton
        aria-label="Next page"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        variant="ghost"
        icon={<ChevronRightIcon />}
      />
    </Flex>
  );
};

export default Pagination;
